import {CSSProperties} from 'react';
import ICON_KAKAOPAGE from '../../../public/static/badge/ico-kakaopage.svg';
import ICON_ADULT from '../../../public/static/v4/badge/icon-adult.svg';
import ICON_WAIT_FOR_FREE_OFF from '../../../public/static/v4/badge/icon-wait-for-free-off.svg';
import ICON_WAIT_FOR_FREE_PLUS_OFF from '../../../public/static/v4/badge/icon-wait-for-free-plus-off.svg';
import ICON_WAIT_FOR_FREE_PLUS from '../../../public/static/v4/badge/icon-wait-for-free-plus.svg';
import ICON_WAIT_FOR_FREE from '../../../public/static/v4/badge/icon-wait-for-free.svg';
import BadgeImgText from '../../common/component/BadgeImgText';
import Text from '../../common/component/Text';
import {
  BadgeClassName,
  BadgeImgTextType,
  BadgeImgType,
  BadgeWaitfree,
} from '../../common/type';

interface IProps extends BadgeClassName {
  variant?: BadgeVariant;
  label: string;
  waitfree?: BadgeWaitfree;
  className?: string;
  imgType?: BadgeImgType;
  imgTextType?: BadgeImgTextType;
  customStyle?: CSSProperties;
  isEllipsis?: boolean;
  isBig?: boolean;
}

export default function Badge({
  variant,
  label,
  waitfree,
  className,
  customTypeClassName,
  variantTypeClassName,
  imgTypeClassName,
  imgTextTypeClassName,
  customStyle,
  imgType,
  imgTextType,
  isEllipsis,
  isBig,
}: IProps) {
  if (customStyle) {
    return (
      <Text
        className={cn(
          'font-badge !whitespace-nowrap rounded-5',
          className,
          customTypeClassName,
          {
            'overflow-hidden text-ellipsis': isEllipsis,
          }
        )}
        style={customStyle}
      >
        {label}
      </Text>
    );
  } else if (variant) {
    const {textClassName, bgClassName, borderClassName, restStyle} =
      BADGE_VARIANT_MAP[variant];
    const height = BADGE_SIZE;
    return (
      <Text
        className={cn(
          'font-badge !whitespace-nowrap rounded-5',
          className,
          variantTypeClassName,
          textClassName,
          bgClassName,
          borderClassName,
          {
            'overflow-hidden text-ellipsis': isEllipsis,
            'px-4': !isBig,
            'px-5 !text-[11px]': isBig,
          }
        )}
        style={{
          height: `${height}px`,
          lineHeight: `${height + (borderClassName ? 0 : 2)}px`,
          ...restStyle,
        }}
      >
        {label}
      </Text>
    );
  } else if (imgType) {
    const info = BADGE_IMG_MAP[imgType];
    const {width, height} = {
      width: info.width,
      height: info.height,
    };
    return (
      <img
        src={info.path}
        alt={label}
        className={cn(className, imgTypeClassName)}
        width={width}
        height={height}
      />
    );
  } else if (imgTextType) {
    const {textClassName, ...restProps} = BADGE_IMG_TEXT_MAP[imgTextType];
    return (
      <BadgeImgText
        className={cn(className, imgTextTypeClassName, {
          'pr-5': isBig,
        })}
        imgTextType={imgTextType}
        label={label}
        waitfree={waitfree}
        height={BADGE_SIZE}
        textClassName={cn(textClassName, {
          '!text-[11px]': isBig,
        })}
        {...restProps}
      />
    );
  }
  return null;
}

interface BadgeStyle {
  bgClassName?: string;
  textClassName: string;
  borderClassName?: string;
  restStyle?: CSSProperties;
}

export type BadgeVariant =
  | 's10_black_white'
  | 's10_darkGrey01_white'
  | 's10_red_white'
  | 's10_red_white_dark'
  | 's10_white_black'
  | 's10_white_black_02'
  | 's10_yellow_darkGrey01'
  | 's10_white_black_03'
  | 's10_grey09_white_dark'
  | 's10_green_white'
  | 's10_blue_white'
  | 's10_orange_white'
  | 's10_grey05_white'
  | 's10_yellow_black'
  | 's10_purple_white'
  | 's10_lightPink_white'
  | 's10_darkBlue_white';

const BADGE_VARIANT_MAP: {
  [key in BadgeVariant]: BadgeStyle;
} = {
  s10_black_white: {
    bgClassName: 'bg-black',
    textClassName: 's10-bold-white',
  },
  s10_darkGrey01_white: {
    bgClassName: 'bg-dark-grey-01',
    textClassName: 's10-bold-white',
  },
  s10_red_white: {
    bgClassName: 'bg-red',
    textClassName: 's10-bold-white',
  },
  s10_red_white_dark: {
    bgClassName: 'bg-dark-red',
    textClassName: 's10-bold-white',
  },
  s10_white_black: {
    bgClassName: 'bg-white light:bg-black',
    textClassName: 's10-bold-black light:text-white',
  },
  s10_white_black_02: {
    bgClassName: 'bg-white',
    textClassName: 's10-bold-black',
    borderClassName: 'border border-solid border-black/25',
  },
  s10_yellow_darkGrey01: {
    bgClassName: 'bg-kakaopage-yellow',
    textClassName: 's10-bold-darkGrey01',
  },
  s10_white_black_03: {
    bgClassName: 'bg-white',
    textClassName: 's10-bold-black',
  },
  s10_grey09_white_dark: {
    bgClassName: 'bg-dark-grey-09',
    textClassName: 's10-bold-white',
  },
  s10_green_white: {
    bgClassName: 'bg-green',
    textClassName: 's10-bold-white',
  },
  s10_blue_white: {
    bgClassName: 'bg-dark-blue',
    textClassName: 's10-bold-white',
  },
  s10_orange_white: {
    bgClassName: 'bg-orange',
    textClassName: 's10-bold-white',
  },
  s10_grey05_white: {
    bgClassName: 'bg-grey-05',
    textClassName: 's10-bold-white',
  },
  s10_yellow_black: {
    bgClassName: 'bg-kakaopage-yellow',
    textClassName: 's10-bold-black',
  },
  s10_purple_white: {
    bgClassName: 'bg-purple',
    textClassName: 's10-bold-white',
  },
  s10_lightPink_white: {
    bgClassName: 'bg-light-pink',
    textClassName: 's10-bold-white',
  },
  s10_darkBlue_white: {
    bgClassName: 'bg-dark-blue',
    textClassName: 's10-bold-white',
  },
};

const BADGE_SIZE = 16;

const BADGE_IMG_MAP: {
  [key in BadgeImgType]: {path: string; width: string; height: string};
} = {
  adult: {
    path: ICON_ADULT,
    width: `${BADGE_SIZE}px`,
    height: `${BADGE_SIZE}px`,
  },
  adultKor: {
    path: ICON_ADULT,
    width: `${BADGE_SIZE}px`,
    height: `${BADGE_SIZE}px`,
  },
  waitForFree: {
    path: ICON_WAIT_FOR_FREE,
    width: `${BADGE_SIZE}px`,
    height: `${BADGE_SIZE}px`,
  },
  waitForFreeTimer: {
    path: ICON_WAIT_FOR_FREE_OFF,
    width: `${BADGE_SIZE}px`,
    height: `${BADGE_SIZE}px`,
  },
  waitForFreeWaiting: {
    path: ICON_WAIT_FOR_FREE_OFF,
    width: `${BADGE_SIZE}px`,
    height: `${BADGE_SIZE}px`,
  },
  kakaopage: {
    path: ICON_KAKAOPAGE,
    width: `${BADGE_SIZE}px`,
    height: `${BADGE_SIZE}px`,
  },
};

const BADGE_IMG_TEXT_MAP: {
  [key in BadgeImgTextType]: {
    path: string;
    textClassName: string;
    bgClassName: string;
  };
} = {
  waitForFreePlus: {
    path: ICON_WAIT_FOR_FREE_PLUS,
    bgClassName: 'bg-white',
    textClassName: 's10-bold-darkGrey01',
  },
  waitForFreePlusTimer: {
    path: ICON_WAIT_FOR_FREE_PLUS_OFF,
    bgClassName: 'bg-white',
    textClassName: 's10-bold-darkGrey01',
  },
  waitForFreePlusExtended: {
    path: ICON_WAIT_FOR_FREE_PLUS,
    bgClassName: 'bg-white',
    textClassName: 's10-bold-darkGrey01',
  },
  waitForFreeExtended: {
    path: ICON_WAIT_FOR_FREE_PLUS,
    bgClassName: 'bg-kakaopage-yellow',
    textClassName: 's11-bold-black',
  },
  waitForFreeNotice: {
    path: ICON_WAIT_FOR_FREE_PLUS,
    bgClassName: 'bg-kakaopage-yellow',
    textClassName: 's10-bold-black',
  },
  waitForFreeNoticeCharging: {
    path: ICON_WAIT_FOR_FREE_PLUS_OFF,
    bgClassName: 'bg-grey-09 light:bg-grey-07',
    textClassName: 's10-bold-white',
  },
};
